/**
 * "Fix it" on the public inspection lookup (owner, 2026-10-13: "a 'fix it' button next to it ... with a faded guide that leads to
 * opacity like a NY Times article paywall"; "gate it from the flow but it's in the library if they search manually elsewhere. The
 * goal is conversion and also to show other restaurants the value of the application").
 *
 * Next to a cited item on /find/ (site/find.js), "Fix it" opens that item's guide inline: the guide's title and ONE real, safe first
 * step in full (a section of the library guide, its own sourced words), then a fade with no text in it, then "Do you work at or own <place>? Fix
 * it" (sign-up with the place and the item carried) and "Run a restaurant? CleanCleaver handles this for yours". The same guide is
 * whole and free in the library (/library/<id>/): nothing here changes the library.
 *
 * Rules (packages/core/test/fixit.test.ts, each with a control):
 * | F1 Facts only        | the cited line stays the department's words; ours only name the guide, its first step and the ask     |
 * | F2 Never a grade      | no grade, ranking or comparison word in anything we write here (FIX_UNSAID)                          |
 * | F3 Safety first       | the first step is shown in full; a guide the library never gates (isLibraryGuideFree) is never faded |
 * | F4 Never invent       | an item with no guide and nothing in the app gets no button (fixItemOf is null)                       |
 * | F5 Nothing hidden     | the text under the fade is not sent at all: the preview carries the first step and a count only       |
 * | F6 Shown sources only | the API adds these only to panels of sources approved for display (displayApprovedOf)               |
 *
 * Guide map (FIX_GUIDES): FDA Form 3-A item (inspectionform.ts FORM_ITEMS) → the library guide and the section that is its first
 * step, picked by hand so the first lines are something a person can do today (pests: "act now", the trash cans; cold food:
 * "chill it right away"). An item with no library guide falls back to what in the app keeps it right (lastinspection.ts
 * INSPECTION_COVER), said plainly; an item with neither has no button. Approved violation articles (content/violation-articles)
 * take over an item's guide here when they land.
 */
import { AHEAD } from "./ahead.ts";
import { INSPECTION_COVER } from "./lastinspection.ts";
import { libraryForState, type LibraryTopic } from "./library.ts";
import { isLibraryGuideFree } from "./leads.ts";
import { fdaItemsOf } from "./official-riskfactors.ts";
import { TRIAL_DAYS } from "./billing.ts";
import { FIXLOG_LABEL } from "./fixgo.ts";
import type { UsState } from "./states/types.ts";
import type { InspectionView, OfficialPanel } from "./official.ts";

/** Which library guide answers an FDA item, and which of its sections is the first step (0-based). */
export interface FixGuideRef { guide: string; section: number; /** Fewer first lines than FIX_FIRST_LINES (a section whose later lines are about something else). */ lines?: number }

export const FIX_GUIDES: Readonly<Record<number, FixGuideRef>> = {
  1: { guide: "food-handler-permits", section: 3 }, // the person in charge
  2: { guide: "food-handler-permits", section: 2, lines: 1 }, // certified food safety manager (line 2 is about the exam)
  3: { guide: "sick-workers", section: 0 },
  4: { guide: "sick-workers", section: 3 },
  5: { guide: "vomit-diarrhea-cleanup", section: 0 },
  6: { guide: "hair-jewelry-nails", section: 3 },
  8: { guide: "handwashing", section: 0 },
  9: { guide: "gloves-bare-hands", section: 0 },
  10: { guide: "handwashing", section: 3 },
  11: { guide: "receiving", section: 2 },
  12: { guide: "receiving", section: 0 },
  13: { guide: "receiving", section: 1 },
  15: { guide: "fridge-storage-order", section: 0 },
  16: { guide: "cleaning-sanitizing", section: 0 },
  17: { guide: "date-marking", section: 3 },
  18: { guide: "cooking-temperatures", section: 0 },
  19: { guide: "reheating", section: 0 },
  20: { guide: "cooling", section: 1 },
  21: { guide: "hot-cold-holding", section: 2 },
  22: { guide: "hot-cold-holding", section: 2 },
  23: { guide: "date-marking", section: 0 },
  24: { guide: "time-as-control", section: 0 },
  28: { guide: "chemicals-sds", section: 0 },
  33: { guide: "cooling", section: 2 },
  35: { guide: "thawing", section: 0 },
  36: { guide: "thermometers", section: 2 },
  37: { guide: "labels", section: 0 },
  38: { guide: "pest-signs", section: 1 },
  39: { guide: "fridge-storage-order", section: 3 },
  40: { guide: "hair-jewelry-nails", section: 2 },
  41: { guide: "wiping-cloths", section: 1 },
  43: { guide: "buffets-self-service", section: 1 },
  46: { guide: "gloves-bare-hands", section: 1 },
  48: { guide: "three-compartment-sink", section: 0 },
  53: { guide: "restroom-mops-rags", section: 2 },
  54: { guide: "restroom-mops-rags", section: 5 },
};

/** How many lines of the first step show (a short section shows whole). */
export const FIX_FIRST_LINES = 3;

/** The words, in one place (copy lint: apps/landing copy-lint; F2: FIX_UNSAID). */
export const FIX_COPY = {
  /** Owner, 2026-10-15: "Fix and log it now" (core fixgo.ts FIXLOG_LABEL: one constant, everywhere). */
  button: FIXLOG_LABEL,
  /** The quiet toggle that opens the first step in full (the gate's preview). */
  peek: "See the first step",
  kicker: "How to fix it",
  claim: (place: string) => `Do you work at or own ${place}? ${FIXLOG_LABEL}`,
  trial: `${TRIAL_DAYS} days free. Cancel anytime.`,
  otherLead: "Run a restaurant?",
  other: "CleanCleaver handles this for yours",
  more: (n: number) => `The full guide has ${n} more part${n === 1 ? "" : "s"}.`,
  unlock: "Sign up to open the full guide in the app.",
  freeLink: "Read the whole guide",
  inApp: "In CleanCleaver:",
  from: (cite: string) => `From ${cite}`,
} as const;

/** F2: words we never write about a place on the lookup (a grade, a ranking, a comparison, or calling it official or passed). "best" is
 * left out: the library says it of methods ("In the fridge (best)"), never of a place. */
export const FIX_UNSAID = /\b(grades?|graded|rank(s|ed|ing)?|scores?|scored|pass(es|ed)?|fail(s|ed)?|official(ly)?|safest|cleanest|dirtiest|worst|better than|worse than|compared?)\b/i;

export interface FixPreview {
  item: number;
  /** "guide": a library guide's first step (faded after it unless the guide is always free). "cover": what in the app keeps it right. */
  kind: "guide" | "cover";
  title: string;
  /** The first step: the section's heading, its first lines (bullets without their dash; numbered lines kept) and the rule it
   * comes from (the section's first code citation, "Food Code 6-501.112"; our internal "Source protocols" lines are not shown). */
  step: { heading: string; lines: string[]; cite: string | null } | null;
  /** Sections after the first step that the fade stands for (a count only: their text is never sent, F5). */
  more: number;
  /** The next section's heading only, never its words (v5.7: the teaser's faded step 2), or null. */
  next: string | null;
  /** The library guide (its page is /library/<id>/), or null for a cover line. */
  guide: string | null;
  /** The guide's section shown as the first step. */
  section: number;
  /** True for a guide the library never gates (sick workers, vomit cleanup, chemicals...): shown without a fade, linked whole (F3). */
  free: boolean;
  /** For "cover": what in the app keeps the item right ("Fridge and freezer temperatures at opening and closing"). */
  cover: string | null;
}

/** The FDA item a department's code maps to that we can say something useful about, or null (F4: no button). */
export function fixItemOf(sourceId: string, code: string): number | null {
  const items = fdaItemsOf(sourceId, code);
  return items.find((n) => FIX_GUIDES[n]) ?? items.find((n) => coverOf(n)) ?? null;
}

/** What in the app keeps an item right, in one line, or null. */
export function coverOf(n: number): string | null {
  const d = INSPECTION_COVER[n];
  if (!d) return null;
  // `base` is one check or a list of them (main, cited-fix-plan): the first one names it.
  const base = Array.isArray(d.base) ? d.base[0] : (d.base as { label: string } | undefined);
  return d.builtin ?? base?.label ?? d.add?.title ?? null;
}

/**
 * Which article answers one citation (owner, 2026-10-13: the article that best matches the inspector's note; none matches well → the
 * item's general article, and the team is told an article is missing). `strength`: "situation" = matched to the note, "general" =
 * the item's general article, "none" = no article (the cover line, or no button).
 *
 * MINIMAL VERSION (item number only): the note matcher is being built on content/violation-articles and replaces this function by
 * the same name; until then every citation gets its item's general library guide and the note is not read.
 */
export interface GuideChoice { articleId: string | null; situation?: string; strength: "situation" | "general" | "none" }
export function guideFor(citation: { item: number; note: string }): GuideChoice {
  const ref = FIX_GUIDES[citation.item];
  return ref ? { articleId: ref.guide, strength: "general" } : { articleId: null, strength: "none" };
}

const clean = (l: string) => l.replace(/^-\s+/, "").trim();

/** The section's first lines: a short section whole, a long one cut at FIX_FIRST_LINES (never mid-line). */
function stepOf(t: LibraryTopic, i: number, max = FIX_FIRST_LINES): FixPreview["step"] {
  const s = t.sections[i];
  if (!s) return null;
  const lines = s.body.split("\n").map(clean).filter(Boolean);
  if (!lines.length) return null;
  return { heading: s.heading, lines: max === FIX_FIRST_LINES && lines.length <= max + 1 ? lines : lines.slice(0, max), cite: s.citations.find((c) => !/^Source protocols/i.test(c)) ?? null };
}

/**
 * The library in a state's words, built once per state (security run-12 F2: the API asked for it once per panel). Only a built
 * library is kept: a state whose pack isn't loaded throws and nothing is cached.
 */
const LIBRARIES = new Map<string, readonly LibraryTopic[]>();
export function libraryOf(state?: UsState): readonly LibraryTopic[] {
  const k = state ?? "UT";
  let lib = LIBRARIES.get(k);
  if (!lib) { lib = libraryForState(state); LIBRARIES.set(k, lib); }
  return lib;
}

/** Where a preview is kept in a panel's `fixes` (one per article and first step, or per item for a cover line). */
export const fixKeyOf = (p: FixPreview): string => (p.guide ? `${p.guide}#${p.section}` : `cover:${p.item}`);

/**
 * One citation's preview for a place in `state` (the library in that state's words), or null when there is nothing to show (F4).
 * The article comes from guideFor; its first step is the section FIX_GUIDES names when it is the item's own guide, else its first.
 */
export function fixPreviewFor(citation: { item: number; note: string }, state?: UsState, library: readonly LibraryTopic[] = libraryOf(state)): FixPreview | null {
  const n = citation.item;
  const g = guideFor(citation);
  const t = g.articleId ? library.find((x) => x.id === g.articleId) : undefined;
  const ref = FIX_GUIDES[n];
  const own = !!(t && ref && ref.guide === t.id);
  const section = own ? ref!.section : 0;
  const step = t ? stepOf(t, section, own ? ref!.lines : undefined) : null;
  const next = t ? (t.sections.find((_, i) => i > section) ?? t.sections.find((_, i) => i !== section))?.heading ?? null : null;
  if (t && step) return { item: n, kind: "guide", title: t.title, step, more: Math.max(0, t.sections.length - 1), next, guide: t.id, section, free: isLibraryGuideFree(t.id), cover: null };
  const cover = coverOf(n);
  const a = AHEAD[n];
  if (!cover || !a) return null;
  return { item: n, kind: "cover", title: a.thing, step: null, more: 0, next: null, guide: null, section: 0, free: false, cover };
}
/** The item's general preview (no note). */
export const fixPreviewOf = (n: number, state?: UsState, library?: readonly LibraryTopic[]): FixPreview | null => fixPreviewFor({ item: n, note: "" }, state, library);

/** The sign-up link's own query for a "Fix it" ask: the place (only for "Do you work at or own …?") and the item. */
export const FIX_PARAMS = { place: "place", item: "fix", home: "home", as: "as" } as const;
/** Who taps "Fix it" on a place (the one question after the ask): the owner, or someone who works there (a manager, a cook). */
export type FixAs = "own" | "work";
export const isFixAs = (v: unknown): v is FixAs => v === "own" || v === "work";
export const PLACE_KEY = /^[a-z0-9][a-z0-9-]{0,39}:[A-Za-z0-9._-]{1,100}$/;
export function fixQuery(item: number, placeKey?: string | null, as?: FixAs | null): string {
  const p = new URLSearchParams();
  const place = placeKey && PLACE_KEY.test(placeKey) ? placeKey : null;
  if (place) p.set(FIX_PARAMS.place, place);
  p.set(FIX_PARAMS.item, String(item));
  if (place && as && isFixAs(as)) p.set(FIX_PARAMS.as, as);
  return p.toString();
}
/** Reads a sign-up link's "Fix it" intent; anything malformed is dropped (never trusted). */
export function fixFromQuery(search: string): { place: string | null; item: number; /** A "For my home" ask: the matching home guide's id, or "1" for none. */ home?: string; /** Who tapped (only with a place): "own" or "work". Absent = the owner's way, as before. */ as?: FixAs } | null {
  const p = new URLSearchParams(search);
  const raw = p.get(FIX_PARAMS.item);
  if (!raw || !/^\d{1,2}$/.test(raw)) return null;
  const item = Number(raw);
  if (item < 1 || item > 56) return null;
  const place = p.get(FIX_PARAMS.place);
  const home = p.get(FIX_PARAMS.home);
  const okPlace = place && PLACE_KEY.test(place) ? place : null;
  const as = p.get(FIX_PARAMS.as);
  return { place: okPlace, item, ...(home !== null && /^(1|[a-z0-9-]{1,40})$/.test(home) ? { home } : {}), ...(okPlace && isFixAs(as) ? { as } : {}) };
}

/**
 * A panel with "Fix it" on each cited line it can speak to (F4) and the previews it needs, once each (`fixes`, keyed by fixKeyOf).
 * Called by the API for panels of sources shown to diners only (F6). The department's words are never changed (F1).
 */
export function withFixes(panel: OfficialPanel, sourceId: string, state?: UsState): OfficialPanel {
  const fixes: Record<string, FixPreview> = {};
  const library = libraryOf(state);
  const mark = (v: InspectionView): InspectionView => ({
    ...v,
    groups: v.groups.map((g) => ({
      ...g,
      lines: g.lines.map((l) => {
        const item = fixItemOf(sourceId, l.code);
        const p = item === null ? null : fixPreviewFor({ item, note: l.detail ?? l.title }, state, library);
        if (!p || item === null) return { ...l, fix: null };
        const key = fixKeyOf(p);
        fixes[key] = p;
        return { ...l, fix: { item, key } };
      }),
    })),
  });
  const latest = panel.latest ? { ...mark(panel.latest), line: panel.latest.line, routineNote: panel.latest.routineNote } : null;
  const items = panel.history.items ? panel.history.items.map(mark) : null;
  return { ...panel, latest, history: { ...panel.history, items }, fixes };
}
